import { useQueries } from "@tanstack/react-query";

import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ENDPOINTS } from "@/api/application-api";
import { authHeader } from "@/utils/headers";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { getEvaluateTagId, EvaluateTagMetadata } from "@/utils/tagUtils";
import { Report } from "@/types/evaluate";

/**
 * Hook to fetch report metadata for evaluate tag IDs
 * Returns a map of report ID to metadata and loading state
 */
export const useEvaluateTagReports = (
  tags: string[],
): {
  metadataMap: Record<string, EvaluateTagMetadata>;
  isLoading: boolean;
} => {
  const userToken = useUserToken();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId;

  // Extract and deduplicate evaluate tag IDs
  const evaluateIds = Array.from(
    new Set(tags.map(tag => getEvaluateTagId(tag)).filter((id): id is string => id !== null)),
  );

  // Fetch metadata for each evaluate ID
  const queries = useQueries({
    queries:
      evaluateIds.length > 0
        ? evaluateIds.map(id => ({
            queryKey: [ENDPOINTS.legacy_reports, id],
            queryFn: async () => {
              if (!userToken) {
                return null;
              }
              const url = `${ENDPOINTS.legacy_reports}/${id}`;
              const response = await fetch(url, {
                headers: authHeader(userToken),
              });
              const data = await handleResponse(response);
              return data?.report as Report | null;
            },
            enabled: !!userToken && !!workspaceId,
            staleTime: 5 * 60 * 1000, // Cache for 5 minutes
          }))
        : [],
  });

  // Build metadata map
  const metadataMap: Record<string, EvaluateTagMetadata> = {};
  let isLoading = false;

  queries.forEach((query, index) => {
    const reportId = evaluateIds[index];
    if (query.isLoading) {
      isLoading = true;
    } else if (query.data && !query.error) {
      metadataMap[reportId] = {
        is_blueprint: query.data.is_blueprint,
        parent_report_id: query.data.parent_report_id,
        workspace_id: query.data.workspace_id,
      };
    }
  });

  return { metadataMap, isLoading };
};
